import { User } from '@choco-admin/types';
import { useCallback, useState, useMemo } from 'react';

import {
  type UsersPageSearchUsersQuery,
  useUsersPageSearchUsersLazyQuery,
} from '../../../generated';
import { mapToUser } from '../utils/userMapper';

export type CaptureExceptionFunction = (error: Error) => void;

export type SearchUsersParams = {
  keywords?: string;
  filter?: {
    isPuppy?: boolean;
  };
  limit?: number;
  after?: string;
};

export type UseSearchUsersResponse = {
  users: User[];
  totalUsers: number;
  isLoading: boolean;
  hasNextPage: boolean;
  searchUsers: (params: SearchUsersParams) => Promise<void>;
  refetch: () => Promise<void>;
};

export type UseSearchUsersConfig = {
  initialPageSize: number;
  captureException?: CaptureExceptionFunction;
};

type UserSearchConnectionResult = UsersPageSearchUsersQuery['userSearch'];

const mapUsers = (
  result: UserSearchConnectionResult | null | undefined
): { users: User[]; total: number } => {
  if (!result || result.__typename !== 'UserSearchConnection') {
    return { users: [], total: 0 };
  }

  const { edges, total } = result;

  try {
    const users = edges.map((edge) => mapToUser(edge.node));
    return { users, total };
  } catch (error) {
    // Return empty result on mapping error
    return { users: [], total: 0 };
  }
};

export const useSearchUsers = (
  config: UseSearchUsersConfig
): UseSearchUsersResponse => {
  const { captureException, initialPageSize } = config;
  const [lastParams, setLastParams] = useState<SearchUsersParams | null>(null);

  const [executeQuery, { data, loading }] = useUsersPageSearchUsersLazyQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const searchUsers = useCallback(
    async (params: SearchUsersParams) => {
      try {
        setLastParams(params);
        const effectivePageSize = params.limit ?? initialPageSize;

        const query = {
          ...(params.keywords && { keywords: params.keywords }),
          filter: {
            ...params.filter,
          },
        };

        await executeQuery({
          variables: {
            query,
            limit: effectivePageSize,
            after: params.after,
          },
        });
      } catch (error) {
        captureException?.(error as Error);
        throw error;
      }
    },
    [executeQuery, captureException, initialPageSize]
  );

  const refetch = useCallback(async () => {
    if (lastParams) {
      await searchUsers(lastParams);
    }
  }, [lastParams, searchUsers]);

  const { users, total } = useMemo(
    () => mapUsers(data?.userSearch),
    [data?.userSearch]
  );

  return useMemo(
    () => ({
      users: users || [], // Ensure we always return an array
      totalUsers: total,
      isLoading: loading,
      hasNextPage: false, // TODO: Implement pagination support
      searchUsers,
      refetch,
    }),
    [users, total, loading, searchUsers, refetch]
  );
};
